<template>
  <div>
    <LoadingIndicator
      v-if="!previewReady && !error"
      :text="$t('Generating preview. This may take a few minutes...')"
      class="print-preview-loading-indicator"
    />
    <div
      v-if="draftLoaded"
      :class="{
        'print-preview--board': !isSlides,
        'print-preview--slides': isSlides,
        'print-preview--overflow-hidden': overflowHidden,
      }"
      class="print-preview"
    >
      <div
        v-for="page in draft.pages"
        :key="page.id"
        class="print-preview__page"
        :class="{
          'print-preview__page--fit-slide-to-page': fitSlideToPage(page),
          'print-preview__page--use-page-breaks': usePageBreaks(page),
        }"
      >
        <StudioEditor
          :load-draft="false"
          :page-id="page.id"
          :toolbar-visible="false"
          :slide-buttons-visible="false"
          :notes-visible="false"
          :use-immersive-reader="false"
          :canvas-padding="0"
          :canvas-border-width="1"
          :background="editorBackground"
          :height="isSlides ? (fitSlideToPage(page) ? '7in' : '5.17in') : undefined"
          :module-animations-enabled="false"
          mode="VIEW"
          embedded
        />

        <!-- Notes Modules
          Notes modules have special formatting for print so are rendered outside
          of the vue-grid-layout canvas.
        -->
        <NotesInline
          v-if="printNotes"
          :page="page"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import {
  every, first, get, uniq,
} from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import chain from '@/lib/utils/chain';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import NotesInline from '@/components/NotesInline.vue';
import { NON_PROCESSING_ASSET_STATUSES } from '@/lib/constants';
import {
  useAppStore,
  useAssessmentStore,
  useAssetStore,
  useEditorStore,
  useErrorStore,
  useUploadStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

const StudioEditor = defineAsyncComponent(() => import(/* webpackChunkName: "editor" */'@/views/StudioEditor.vue'));

/*
  Any tags in the DOM expected to have a CSS style that includes
  background: url(...) or background-image: url(...).
*/
const TAGS_WITH_BACKGROUND_IMAGES = ['div'];

export default {
  name: 'PrintPreview',
  components: {
    StudioEditor,
    LoadingIndicator,
    NotesInline,
  },
  data() {
    return {
      editorBackground: nebulaTokens.color.white,
      sortedNotesByPageId: {},
      imagesLoaded: false,
      assetsAndUploads: null,
      /*
        Set overflow hidden to all elements when printing in firefox. This
        works around a bug in firefox where image data is lost when an
        image extends across more than one page.
      */
      overflowHidden: navigator.userAgent.includes('Firefox'),
      /*
        Don't attempt to fit one slide per page if we're using Safari since
        we don't have control of print page margins. Otherwise, slide content
        with margins could be larger than one page which will cause layout problems.
      */
      canFitSlideToPage: !(navigator.vendor && navigator.vendor.includes('Apple')
        && !navigator.userAgent.includes('CriOS')
        && !navigator.userAgent.includes('FxiOS')),
    };
  },
  async created() {
    /*
      Track thumbnail loaders so we can wait for them to complete
      before rendering the print preview.
    */
    this[types.SET_TRACK_THUMBNAIL_LOADERS](true);
    /*
      We'll either load a draft or a creation from the server depending on
      whether the print preview was requested from edit or view mode.
    */
    if (this.type === 'creation') {
      // If we're loading a creation, also get the attempt if available.
      const allowAttempt = (this.userIsStudent && this.$route.query.homework_id)
        || (!this.userIsStudent
            && (this.$route.query.attempt || this.$route.query.homework_id));
      const payload = {
        allowAttempt,
        id: this.$route.query.id,
        homeworkId: this.$route.query.homework_id,
        userId: this.$route.query.user_id,
        ignoreGetAttemptError: true,
        useRevision: allowAttempt || this.$route.query.use_revision,
      };
      this[types.SET_ALLOW_EDIT_ATTEMPT](false);
      await this[types.GET_CREATION](payload);
    } else {
      await this[types.GET_DRAFT]({
        draftId: this.id,
        commitOnSuccess: true,
      });
    }

    document.title = `${this.draftName} | ${this.$t('Studio | Discovery Education')}`;
  },
  watch: {
    previewReady() {
      // When the preview is ready, open the browser print dialog.
      if (this.previewReady) {
        setTimeout(() => {
          window.print();
        }, 500);
      }
    },
    draftLoaded() {
      /*
        Once the draft is loaded from the server into the app store,
        scan the draft for all referenced asset ids and upload ids. We'll
        wait until these are loaded before showing the preview.
      */
      if (this.draftLoaded) {
        this.assetsAndUploads = this.draft.pages.reduce((acc, page) => {
          // Include asset and upload modules
          [...page.modules, ...(page.notes || [])].forEach((mod) => {
            if (['asset', 'upload'].includes(mod.type) && mod.options?.asset_id) {
              acc.assetIds.push(mod.options?.asset_id);
            } else if (mod.type === 'upload' && mod.options?.upload_id) {
              acc.uploadIds.push(mod.options?.upload_id);
            } else if (mod.type === 'block') {
              const teiId = chain(mod.references)
                .find((ref) => ref.reference_type === 'asset')
                .get('reference_id')
                .value();
              if (teiId) {
                const moduleData = chain(this)
                  .get('attempt.items')
                  .find((item) => item.tei_id === teiId)
                  .get('try.response.moduleData')
                  .value();
                const assetId = get(moduleData, 'assetId');
                const uploadId = get(moduleData, 'uploadId');
                if (assetId) acc.assetIds.push(assetId);
                if (uploadId) acc.uploadIds.push(uploadId);
              }
            }
          });

          // Include background images
          if (get(page, 'options.theme.background_image')) {
            acc.assetIds.push(page.options.theme.background_image);
          }

          // Include full-slide assets
          if (get(page, 'options.asset_id')) {
            acc.assetIds.push(page.options.asset_id);
          }

          return acc;
        }, { assetIds: [], uploadIds: [] });
      }
    },
    assetsAndUploadsLoaded() {
      if (this.assetsAndUploadsLoaded) {
        setTimeout(async () => {
          /*
            Need to wait for all thumbnail loaders to finish running,
            otherwise image urls might not be set in the DOM before
            awaiting 'waitForImagesToLoad() below'.
          */
          await this.waitForThumbnailLoadersToFinish();

          setTimeout(async () => {
            // Wait for all the images in the DOM to load.
            await this.waitForImagesToLoad();
            this.imagesLoaded = true;
          }, 500);
        }, 500);
      }
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.GET_DRAFT,
      types.GET_CREATION,
      types.SET_TRACK_THUMBNAIL_LOADERS,
    ]),
    ...mapActions(useAssessmentStore, [
      types.SET_ALLOW_EDIT_ATTEMPT,
    ]),
    waitForThumbnailLoadersToFinish() {
      /*
        Thumbnail loaders run async while attempting to set a thumbnail
        image url in the DOM. We need wait for all these processes to finish
        before rendering the print preview.
      */
      const thumbnailLoaderPromises = this.thumbnailLoaders
        .filter((loader) => (
          this.assetsAndUploads.assetIds.find((assetId) => loader.id === assetId)
            || this.assetsAndUploads.uploadIds.find((uploadId) => loader.id === uploadId)
        ))
        .map((loader) => loader.promise);
      return Promise.allSettled(thumbnailLoaderPromises);
    },
    waitForImagesToLoad() {
      /*
        Returns a promise that resolves when all images in
        the DOM have finished loading. This is required in order for
        the images to show up in the print preview dialog.
      */
      return new Promise((resolve) => {
        const urls = this.getImageUrls();
        if (!urls.length) {
          resolve();
          return;
        }

        let loadCount = 0;
        this.images = [];

        const onLoad = () => {
          loadCount += 1;
          if (loadCount === urls.length) {
            resolve();
          }
        };

        urls.forEach((url) => {
          const image = new Image();
          image.src = url;
          image.onload = onLoad;
          image.onerror = onLoad;
          this.images.push(image);
        });
      });
    },
    getImageUrls() {
      // Scan the DOM for image urls and build a unique list.
      const tags = [...TAGS_WITH_BACKGROUND_IMAGES, 'img', 'use'];
      const urls = [];
      tags.forEach((tag) => {
        const elements = Array.from(this.$el.getElementsByTagName(tag));
        elements.forEach((el) => {
          if (el.offsetParent === null) return;
          const tagName = el.tagName.toLowerCase();
          if (tagName === 'img') {
            urls.push(el.src);
          } else if (tagName === 'use') {
            const svgSrc = el.getAttribute('xlink:href');
            if (svgSrc) urls.push(first(svgSrc.split('#')));
          } else {
            const computed = window.getComputedStyle(el);
            let background = computed.getPropertyValue('background') || '';
            if (!background.includes('url')) background = computed.getPropertyValue('background-image') || '';
            if (background.includes('url')) {
              const backgroundUrl = background.match(/url\(['"]?(.+?)['"]?\)/)[1];
              urls.push(backgroundUrl);
            }
          }
        });
      });
      return uniq(urls);
    },
    fitSlideToPage(page) {
      return this.canFitSlideToPage && this.isSlides && !this.pageHasNotes(page);
    },
    usePageBreaks(page) {
      /*
        Don't use page breaks for boards or slides with notes in Firefox since it creates
        overlapping content when content is taller than one printable page.
      */
      return !(navigator.userAgent.includes('Firefox')
        && (!this.isSlides || this.pageHasNotes(page)));
    },
    pageHasNotes(page) {
      if (!this.printNotes) return false;

      // Only text, asset and upload modules are printable.
      return (page.notes || []).some((mod) => ['text', 'asset', 'upload'].includes(mod.type));
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'draft',
      'thumbnailLoaders',
      'draftType',
      'pagesSorted',
      'draftName',
    ]),
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useUploadStore, [
      'uploads',
    ]),
    ...mapState(useErrorStore, [
      'error',
    ]),
    ...mapState(useAppStore, [
      'loading',
    ]),
    ...mapState(useAssessmentStore, [
      'attempt',
    ]),
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    id() {
      return get(this, '$route.query.id');
    },
    type() {
      return get(this, '$route.query.type');
    },
    printNotes() {
      return get(this, '$route.query.printNotes') === 'true';
    },
    draftLoaded() {
      return this.draft && Object.keys(this.draft).length;
    },
    assetsAndUploadsLoaded() {
      if (!this.assetsAndUploads) return false;

      const assetIsLoadedOrError = (assetId) => NON_PROCESSING_ASSET_STATUSES.includes(get(this, `assets[${assetId}].status.id`))
          || get(this, `assets[${assetId}].error`);
      /*
        Return true when all assets and uploads have been downloaded
        and have a non processing status or error.
      */
      return every(this.assetsAndUploads.assetIds, assetIsLoadedOrError)
        && every(this.assetsAndUploads.uploadIds, (uploadId) => this.uploads[uploadId]);
    },
    previewReady() {
      /*
        Wait for the draft, all referenced assets and uploads, and all images
        in the DOM to be fully loaded before showing the preivew.
      */
      return this.draftLoaded && this.assetsAndUploadsLoaded
        && this.imagesLoaded && !this.loading;
    },
    isSlides() {
      return this.draftType !== 'board';
    },
  },
};
</script>

<style lang="stylus">
.print-preview-loading-indicator {
  z-index: 551; // above other loading indicators
}

.print-preview {
  /*
    Force background images to render regardless of setting in
    browser's print dialog box.
    NOTE: These options are not guaranteed to work in the future but
    seem to be the best option for now since there isn't a way to
    directly set options for background images in the browser's print dialog.
  */
  -webkit-print-color-adjust: exact !important; // Chrome, Safari
  color-adjust: exact !important; // Firefox

  &__page {
    margin-bottom: $nebula-space-2x;

    &--fit-slide-to-page {
      height: 7in;
    }

    &--use-page-breaks {
      break-inside: avoid;

      &:not(:first-child) {
        page-break-before: always;
      }

      &:not(:last-child) {
        page-break-after: always;
      }
    }
  }

  &--board {
    .print-preview__page {
      page: board;
      width: 7in;
    }

    @page board {
      size: 8.5in 11in;
    }
  }

  &--slides {
    .print-preview__page {
      page: slides;
      width: 9.5in;
    }

    @page slides {
      size: 11in 8.5in;
    }
  }

  &--overflow-hidden * {
    /*
      Fixes issue in firefox where images that extend
      past the current page don't continue to render on
      the next page. Instead cropped area goes missing.
    */
    overflow: hidden !important;
  }

  @page {
    margin: 0.75in;
  }
}

@media print {
  .print-preview  {
    .print-preview__page.print-preview__page--use-page-breaks {
      margin-bottom: 0;
    }
  }
}
</style>
